import type { EditorSelectionState } from "@/commands/Command";
import type { ReorderPlacement } from "@/commands/nodeCommands";
import type { AnimationGraph } from "@/document/model/animation";
import { getActivePageSceneNodes, getAllSceneNodes, type CurvaDocument } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import type { PrimitiveDefinition } from "@/document/model/primitives";
import type { NodeStyle } from "@/document/model/styles";
import type { RichTextModel } from "@/document/model/text";
import type { Rect } from "@/lib/math/rect";

export function toggleSelection(selection: string[], nodeId: string): string[] {
  return selection.includes(nodeId) ? selection.filter(id => id !== nodeId) : [...selection, nodeId];
}

export function titleCase(value: string): string {
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
}

export function reorderMessage(count: number, placement: ReorderPlacement): string {
  const subject = count > 1 ? `${count} nodes` : "Node";
  switch (placement) {
    case "front":
      return `Brought ${subject} to front`;
    case "back":
      return `Sent ${subject} to back`;
    case "forward":
      return `Brought ${subject} forward`;
    case "backward":
      return `Sent ${subject} backward`;
  }
}

export function nodesInLayerOrder(document: CurvaDocument, nodeIds: string[]): SceneNode[] {
  const selected = new Set(nodeIds);
  return getAllSceneNodes(document).filter(node => selected.has(node.id));
}

export function topFrameIdAtPoint(document: CurvaDocument, point: { x: number; y: number }, activePageId?: string): string | undefined {
  return [...getActivePageSceneNodes(document, activePageId)]
    .reverse()
    .find(node => node.type === "frame" && pointInRect(point, node.bounds))
    ?.id;
}

export function preferredAnimationRootForAnimationMode(
  document: CurvaDocument,
  graph: AnimationGraph,
  selection: {
    selectedNodeIds: string[];
    primarySelectedNodeId?: string;
    viewportCenter?: { x: number; y: number };
    activePageId?: string;
  },
): string | undefined {
  const selectedFrameId = selectedFrameIdForAnimationMode(document, selection.selectedNodeIds, selection.primarySelectedNodeId);
  const selectedRootId = animationRootIdForFrame(graph, selectedFrameId);
  if (selectedRootId) {
    return selectedRootId;
  }

  const viewportFrameId = selection.viewportCenter
    ? topFrameIdAtPoint(document, selection.viewportCenter, selection.activePageId)
    : undefined;
  return animationRootIdForFrame(graph, viewportFrameId);
}

export function boundsEqual(left: Rect, right: Rect): boolean {
  return left.x === right.x && left.y === right.y && left.width === right.width && left.height === right.height;
}

export function transformEqual(left: SceneNode["transform"], right: SceneNode["transform"]): boolean {
  return left.x === right.x
    && left.y === right.y
    && left.rotation === right.rotation
    && left.scaleX === right.scaleX
    && left.scaleY === right.scaleY;
}

export function selectionEqual(left: EditorSelectionState, right: EditorSelectionState): boolean {
  return (
    left.activePageId === right.activePageId &&
    arraysEqual(left.selectedNodeIds, right.selectedNodeIds) &&
    left.primarySelectedNodeId === right.primarySelectedNodeId &&
    left.mode === right.mode &&
    left.selectedVertexId === right.selectedVertexId &&
    arraysEqual(left.selectedVertexIds ?? [], right.selectedVertexIds ?? []) &&
    arraysEqual(left.selectedEdgeIds ?? [], right.selectedEdgeIds ?? []) &&
    selectionHandleEqual(left.selectedHandle, right.selectedHandle) &&
    arraysEqual(left.selectedAnimationNodeIds ?? [], right.selectedAnimationNodeIds ?? []) &&
    left.primarySelectedAnimationNodeId === right.primarySelectedAnimationNodeId &&
    arraysEqual(left.selectedAnimationConnectionIds ?? [], right.selectedAnimationConnectionIds ?? []) &&
    left.animationPanelOpen === right.animationPanelOpen
  );
}

export function styleEqual(left: NodeStyle, right: NodeStyle): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function primitiveEqual(left: PrimitiveDefinition, right: PrimitiveDefinition): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function textEqual(left: RichTextModel, right: RichTextModel): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function selectionHandleEqual(
  left: EditorSelectionState["selectedHandle"],
  right: EditorSelectionState["selectedHandle"],
): boolean {
  return left?.edgeId === right?.edgeId && left?.endpoint === right?.endpoint;
}

function arraysEqual(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function selectedFrameIdForAnimationMode(document: CurvaDocument, selectedNodeIds: string[], primarySelectedNodeId?: string): string | undefined {
  const primary = primarySelectedNodeId ? getAllSceneNodes(document).find(node => node.id === primarySelectedNodeId) : undefined;
  if (primary?.type === "frame") {
    return primary.id;
  }
  const selected = new Set(selectedNodeIds);
  return getAllSceneNodes(document).find(node => node.type === "frame" && selected.has(node.id))?.id;
}

function animationRootIdForFrame(graph: AnimationGraph, frameId: string | undefined): string | undefined {
  if (!frameId) {
    return undefined;
  }
  const root = graph.nodes.find(node => node.kind === "animation" && node.frameId === frameId);
  return root?.kind === "animation" ? root.id : undefined;
}

function pointInRect(point: { x: number; y: number }, rect: Rect): boolean {
  const x1 = Math.min(rect.x, rect.x + rect.width);
  const x2 = Math.max(rect.x, rect.x + rect.width);
  const y1 = Math.min(rect.y, rect.y + rect.height);
  const y2 = Math.max(rect.y, rect.y + rect.height);
  return point.x >= x1 && point.x <= x2 && point.y >= y1 && point.y <= y2;
}
